import {
  TASK_SOURCE_LABELS,
  type PoolKey,
  type TaskPriority,
  type TaskSource,
} from '@netcon-work/shared'
import type { ReactNode } from 'react'
import { cn } from '../../lib/cn'

/**
 * Bausteine aus dem Mockup. Die Klassen entsprechen den dortigen
 * Komponenten (.badge, .prio, .pooltag, .card, .btn) – wer die Optik ändern
 * will, ändert erst das Mockup.
 */

/* ── Karte ──────────────────────────────────────────────────────────── */

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn('rounded-card border border-line bg-surface shadow-card', className)}>
      {children}
    </div>
  )
}

export function CardPad({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('px-[18px] py-4', className)}>{children}</div>
}

/* ── Quellen-Badge ──────────────────────────────────────────────────── */

const QUELLEN_STIL: Record<TaskSource, string> = {
  gitlab: 'bg-[#fdeee7] text-[#b6430f]',
  netcon_ticket: 'bg-[#e8f1fc] text-accent-deep',
  work: 'bg-[#e5f7f0] text-[#0d7a52]',
  /**
   * **Derselbe Grünton wie `work`, nur blasser.** Beide Kanäle entstehen in Work; das
   * Formular ist der Weg für Leute, die nicht selbst daran arbeiten. Eine völlig eigene Farbe
   * behauptete einen Unterschied, den es beim Lesen einer Aufgabenliste nicht gibt.
   */
  work_formular: 'bg-[#eef9f4] text-[#0d7a52]',
  /**
   * **Wieder derselbe Grünton** (19.08.2026): eine Mail an das Work-Postfach ist derselbe
   * Weg wie das Formular, nur ohne Maske. Wer eine Aufgabenliste überfliegt, unterscheidet
   * „von innen gemeldet" von „aus einem Quellsystem importiert" – und nicht, welches
   * Eingabefeld benutzt wurde.
   */
  work_mail: 'bg-[#eef9f4] text-[#0d7a52]',
  /**
   * **Outlook-Blau, kein Work-Grün** (Z-17): die Aufgabe entsteht nicht in Work, sondern aus
   * einer Kennzeichnung im eigenen Postfach – ein Quellsystem wie GitLab, nur persönlich.
   */
  outlook: 'bg-[#e6f0fb] text-[#0f5fa8]',
  /** To Do trägt Microsofts Blau eine Stufe dunkler – dieselbe Familie, ein anderer Ort. */
  todo: 'bg-[#e6ecfb] text-[#3b4fb8]',
  notizbuch: 'bg-[#fdf3dc] text-[#8a6100]',
}

export function SourceBadge({
  source,
  label,
}: {
  source: TaskSource | string
  /** Menschenlesbarer Bezug wie „GitLab !312". Fehlt er, kommt der Quellenname. */
  label?: string | null
}) {
  const stil = QUELLEN_STIL[source as TaskSource] ?? 'bg-[#f0efec] text-ink2'
  const text = label ?? TASK_SOURCE_LABELS[source as TaskSource] ?? source
  return (
    <span
      className={cn(
        'inline-flex items-center gap-[5px] rounded-md px-2 py-[2.5px] text-[10.5px] font-semibold',
        stil,
      )}
    >
      {text}
    </span>
  )
}

/* ── Priorität ──────────────────────────────────────────────────────── */

const PRIO_STIL: Record<TaskPriority, string> = {
  A: 'bg-[#fdeaea] text-[#b02a2a]',
  B: 'bg-[#fdf3dc] text-[#8a6100]',
  C: 'bg-[#f0efec] text-[#6d6b65]',
}

export function PrioBadge({ priority }: { priority: TaskPriority }) {
  return (
    <span
      className={cn(
        'inline-flex rounded-md px-[7px] py-[2.5px] text-[10.5px] font-bold',
        PRIO_STIL[priority],
      )}
      title={`Priorität ${priority}`}
    >
      {priority}
    </span>
  )
}

/* ── Pool-Marke ─────────────────────────────────────────────────────── */

/**
 * Nur die **Optik** der Pools. Beschriftung, Symbol und Spielregeln kommen aus
 * der Datenbank (Pools sind konfigurierbar, siehe F-07) – hier stehen die
 * Farbklassen, die es dafür im Mockup gibt.
 *
 * Der Typ `PoolKey` kommt aus dem geteilten Paket, damit es nicht zwei Listen
 * gibt, die auseinanderlaufen können.
 */
export const POOLS: Record<PoolKey, { label: string; icon: string; stil: string; punkt: string }> =
  {
    sofort: {
      label: 'Sofort',
      icon: '🔥',
      stil: 'bg-pool-sofort-bg text-pool-sofort',
      punkt: 'bg-pool-sofort',
    },
    tickets: {
      label: 'Tickets',
      icon: '🎫',
      stil: 'bg-pool-tickets-bg text-pool-tickets',
      punkt: 'bg-pool-tickets',
    },
    projekte: {
      label: 'Projekte',
      icon: '📦',
      stil: 'bg-pool-projekte-bg text-pool-projekte',
      punkt: 'bg-pool-projekte',
    },
    /**
     * **Vorläufige Optik** (20.08.2026). `POOL_KEYS` bekam in einer Parallelsitzung einen
     * vierten Pool; dieser Eintrag fehlte, und der Typ verlangt ihn – der Web-Typecheck war
     * dadurch rot und nichts ließ sich ausliefern.
     *
     * Bewusst **grau statt einer neuen Farbe**: die drei vorhandenen sind gemessen (All-Pairs
     * gegen Farbfehlsichtigkeit, siehe AP 2.3), eine vierte dazuzuerfinden hieße, diesen
     * Nachweis stillschweigend zu entwerten. Grau ist außerdem die richtige Aussage für einen
     * Parkplatz: dort liegt, was gerade **nicht** dran ist. Wer die Farbe setzt, misst sie neu.
     */
    parkplatz: {
      label: 'Parkplatz',
      icon: '🅿️',
      stil: 'bg-[#f3f2ef] text-ink2',
      punkt: 'bg-muted',
    },
  }

export function PoolTag({ pool }: { pool: PoolKey }) {
  const p = POOLS[pool]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-[5px] rounded-full px-[9px] py-[2.5px] text-[10.5px] font-semibold',
        p.stil,
      )}
    >
      <i className={cn('size-1.5 rounded-full', p.punkt)} />
      {p.label}
    </span>
  )
}

/* ── Chip ───────────────────────────────────────────────────────────── */

export function Chip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-block rounded-full bg-[#f3f2ef] px-[10px] py-[2.5px] text-[11px] text-ink2">
      {children}
    </span>
  )
}

/* ── Schaltfläche ───────────────────────────────────────────────────── */

type ButtonVariant = 'default' | 'primary' | 'ghost' | 'soft'

const BUTTON_STIL: Record<ButtonVariant, string> = {
  default: 'bg-ink text-white',
  primary: 'bg-accent text-white',
  ghost: 'border border-line2 bg-transparent text-ink2',
  soft: 'bg-[#f3f2ef] text-ink',
}

export function Button({
  variant = 'default',
  size = 'md',
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: 'sm' | 'md'
}) {
  return (
    <button
      className={cn(
        'cursor-pointer rounded-[9px] font-semibold transition-[filter] hover:brightness-105',
        size === 'sm' ? 'rounded-lg px-[11px] py-[5px] text-xs' : 'px-[15px] py-2 text-[13px]',
        /**
         * **`tap` steht hier und nicht an 200 Aufrufstellen** (Z-10.5, 28.08.2026).
         *
         * Ein `<Button>` ist per Definition eine Handlung – genau das Kriterium, nach dem in
         * `theme.css` entschieden wird, was ein Berührziel ist. Damit erfüllt jeder Knopf die
         * 40 px auf Tastgeräten, ohne dass jemand daran denken muss, und der nächste
         * bekommt sie umsonst.
         *
         * **Am Rechner ändert sich nichts**: die Regel hängt an `pointer: coarse`, nicht an
         * der Fensterbreite. Ein kleines Fenster auf dem Bildschirm bleibt kompakt, ein Tablet
         * im Querformat wird fingerfreundlich – das ist die richtige Frage.
         *
         * Was hier **nicht** greift, sind die rohen `<button>` in den Listen: Aufgabentitel,
         * Pool-Marken, Spaltenköpfe. Das ist Absicht und in `theme.css` begründet.
         */
        'tap',
        BUTTON_STIL[variant],
        className,
      )}
      {...props}
    />
  )
}

/* ── Abschnittsüberschrift ──────────────────────────────────────────── */

export function SectionTitle({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (
    <h2 className="mt-[26px] mb-3 flex items-baseline gap-2 text-sm font-bold tracking-[-0.1px]">
      {children}
      {hint && <small className="text-xs font-normal text-muted">{hint}</small>}
    </h2>
  )
}

/* ── Platzhalter für noch leere Ansichten ───────────────────────────── */

/**
 * **Seit dem 03.08.2026 unbenutzt** – und das ist die Nachricht.
 *
 * Alle neun Ansichten aus dem Konzept tragen jetzt Inhalt: „Mein Bereich" das Melde-Formular
 * (Phase 6), „Vorhaben" und „Planung" seit Phase 8. `Platzhalter.tsx` ist ersatzlos entfallen.
 *
 * Der Baustein bleibt stehen, weil Phase 9 und 10 noch kommen und leere Flächen dann wieder
 * besser sind als halbfunktionale Attrappen.
 */
export function ComingSoon({ phase, children }: { phase: string; children: ReactNode }) {
  return (
    <Card className="mt-1">
      <CardPad>
        <div className="text-[13px] font-semibold">{children}</div>
        <div className="mt-1 text-xs text-muted">Entsteht in {phase}.</div>
      </CardPad>
    </Card>
  )
}
